/**
 * Which fields a buyer's login may not see: on a company's "Buys from us" tab
 * (`buyers.settings.hidden_fields`) and on a stream's Settings tab, as a default
 * for every buyer on it (`campaigns.settings.buyer_hidden_fields`).
 *
 * Uses the same field dropdown as the filter editor: labels over keys, free
 * typing, and keys are what is stored. The server enforces all of this; a buyer
 * login never reads the list. See docs/DOMAIN.md, Hidden fields.
 */
import { useRef, useState } from 'react';
import { Alert, Anchor, Button, CloseButton, Group, Modal, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useHiddenFieldDelivery } from '../api/leadFields';
import { useVocab } from '../vocab';
import { FieldInput, builtInLabel, useLeadFieldSource } from './SmartFilterEditor';

const KEY = /^[A-Za-z0-9_]{1,64}$/;

/** Built-in columns a buyer usually needs: hiding one asks first. */
const USUALLY_NEEDED = new Set([
  'email', 'phone', 'phone1', 'firstname', 'first_name', 'lastname', 'last_name', 'fullname', 'name', 'company',
]);

/** Columns the server refuses: the app can't show a row without them. */
const NOT_HIDEABLE = new Set([
  'id', 'lead_id', 'campaign_id', 'account_id', 'created_at', 'status', 'delivery_status', 'sold',
  'revenue', 'payout', 'buyer_id', 'buyer_name', 'route_id', 'import_id', 'lead_type', 'lead_method',
  'custom_data', 'custom_fields', 'distribution_data', 'score',
]);

/** The stored list, whatever a stored setting holds. */
export function hiddenFieldsOf(settings: unknown, key: string): string[] {
  const raw = (settings as Record<string, unknown> | null | undefined)?.[key];
  return Array.isArray(raw) ? raw.filter((k): k is string => typeof k === 'string') : [];
}

export function HiddenFieldsEditor({
  value, onChange, disabled = false, buyerId, campaignId, label, description,
}: {
  value: string[];
  onChange: (keys: string[]) => void;
  disabled?: boolean;
  /** Warn about the routes sending to this buyer. */
  buyerId?: string;
  /** Warn about this stream's routes, and offer its fields first. */
  campaignId?: string;
  label: string;
  description: string;
}) {
  const v = useVocab();
  const fields = useLeadFieldSource(campaignId ? [campaignId] : undefined);
  const delivery = useHiddenFieldDelivery(value, { buyerId, campaignId });
  const [typing, setTyping] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const justPicked = useRef(false);

  const labelOf = (key: string): string => {
    for (const g of fields.groups) {
      const hit = g.items.find((i) => i.value === key);
      if (hit && hit.label !== key) return hit.label;
    }
    return USUALLY_NEEDED.has(key) ? builtInLabel(v, key) : key;
  };

  function add(raw: string) {
    const key = raw.trim();
    setProblem(null);
    if (!key) return;
    if (!KEY.test(key)) {
      setProblem('Use letters, digits and underscores.');
      return;
    }
    if (NOT_HIDEABLE.has(key.toLowerCase())) {
      setProblem(`The app can't show a ${v.one('lead')} without "${key}", so it can't be hidden.`);
      return;
    }
    setTyping('');
    if (value.includes(key)) return;
    if (USUALLY_NEEDED.has(key.toLowerCase())) {
      setConfirming(key);
      return;
    }
    onChange([...value, key]);
  }

  /** A pick from the list adds straight away; typing waits for Enter. */
  function onPick(key: string) {
    justPicked.current = true;
    add(key);
  }
  function onInput(next: string) {
    setTyping(justPicked.current ? '' : next);
    justPicked.current = false;
  }

  const routes = delivery.data?.routes ?? [];

  return (
    <Stack gap="xs" data-testid="hidden-fields">
      {!disabled && (
        <div onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(typing); } }}>
          <FieldInput
            label={label}
            description={description}
            placeholder="Pick a field, or type a name and press Enter"
            fields={fields}
            value={typing}
            onChange={onInput}
            onPick={onPick}
            testId="hidden-field-input"
          />
          {problem && <Text size="xs" c="red" mt={4}>{problem}</Text>}
        </div>
      )}
      {disabled && <Text size="sm" fw={500}>{label}</Text>}
      {value.length === 0 ? (
        <Text size="xs" c="dimmed">Nothing is hidden.</Text>
      ) : (
        <Group gap={6}>
          {value.map((key) => (
            <Group
              key={key} gap={4} wrap="nowrap" pl={8} pr={disabled ? 8 : 2} py={2}
              style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 6 }}
            >
              <div>
                <Text size="xs" lh={1.2}>{labelOf(key)}</Text>
                {labelOf(key) !== key && <Text size="xs" c="dimmed" lh={1.2}>{key}</Text>}
              </div>
              {!disabled && (
                <CloseButton
                  size="xs"
                  aria-label={`Show ${key} again`}
                  onClick={() => onChange(value.filter((k) => k !== key))}
                />
              )}
            </Group>
          ))}
        </Group>
      )}
      {routes.length > 0 && (
        <Alert color="yellow" variant="light" data-testid="hidden-still-delivered" p="xs">
          <Text size="xs" fw={600}>
            Still being posted
          </Text>
          <Text size="xs" mb={4}>
            {`Hiding a field in the login doesn't stop it being posted. To stop that too, change the ${v.one('route')}.`}
          </Text>
          <Stack gap={2}>
            {routes.map((r) => (
              <Text key={r.id} size="xs">
                <Anchor component={Link} to={`/destinations/${r.id}`} size="xs" c="inherit" underline="always">
                  {r.name || r.id}
                </Anchor>
                {r.active ? '' : ' (off)'}
                {`: ${r.keys.join(', ')}`}
                {r.whole_row ? ` (it sends the whole ${v.one('lead')})` : ''}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}
      <Modal
        opened={confirming !== null}
        onClose={() => setConfirming(null)}
        title={`Hide ${confirming ? labelOf(confirming).toLowerCase() : ''}?`}
        size="sm"
      >
        <Stack>
          <Text size="sm">
            {`A ${v.one('buyer')} usually needs this to use what it bought. Their login won't show it anywhere: not on the ${v.one('lead')}, in an export or in a search.`}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirming(null)}>Keep showing it</Button>
            <Button
              color="red"
              onClick={() => {
                if (confirming) onChange([...value, confirming]);
                setConfirming(null);
              }}
            >
              Hide it
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
